import React, { useCallback, memo, FC } from 'react';
import { cx } from 'emotion';
import { useSurveyConfig } from 'app/core/survey-config';
import Media from './Media';
import { OptionProps } from './types';
import { useStyles } from './styles';

export function createOption<O extends OptionProps>(
  Switcher: FC<Omit<O, keyof OptionProps> & { checked: O['checked'] }>
) {
  function Option({
    Id,
    value,
    label = '',
    checked,
    imageLink = '',
    youtubeID = '',
    className = '',
    textClassName = '',
    disabled = false,
    disabledText = '',
    excluded = false,
    children,
    onChange,
    ...rest
  }: O) {
    const { wording } = useSurveyConfig();
    const onClick = useCallback(() => {
      if (!disabled) {
        onChange(value, !checked);
      }
    }, [value, checked, disabled, onChange]);
    const styles = useStyles([checked, disabled, excluded]);

    return (
      <div
        className={cx(styles.container, className)}
        onClick={onClick}
        data-subject-option-id={Id}
      >
        {(imageLink || youtubeID) && (
          <Media imageLink={imageLink} youtubeID={youtubeID} />
        )}
        <span className={styles.middle}>
          <span className={styles.iconContainer}>
            <Switcher checked={checked} {...rest} />
          </span>
          {label !== '' && (
            <span className={cx(styles.text, textClassName)}>
              {label}
              {disabled && (
                <>
                  <br />
                  <span className={styles.disabledText}>
                    {disabledText === ''
                      ? wording.message.error.option_fulled
                      : disabledText}
                  </span>
                </>
              )}
            </span>
          )}
        </span>
        {children && <div className={styles.childrenContainer}>{children}</div>}
      </div>
    );
  }

  return memo(Option);
}
